import { useEffect, useMemo, useState } from "react";
import {
  Company,
  CreateCompanyData,
  Address,
  CustomFieldValues,
  IsoCountryCode,
  ISO_3166_1_ALPHA_2_CODES,
  isISO31661Alpha2Code,
} from "@forgepoint/shared";
import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UserSelector } from "@/components/UserSelector";
import { PriceListSelector } from "@/components/PriceListSelector";
import { CustomFieldsSection } from "@/components/customFields/CustomFieldsSection";
import { useCompanySettingsStore } from "@/store/companySettingsStore";
import { useCompaniesStore } from "./companiesStore";
import { usePriceListsStore } from "@/modules/priceLists/priceListsStore";

/**
 * Form data structure for creating a company.
 */
interface CompanyFormData extends CreateCompanyData {
  defaultPriceListId: string | null;
  customFields: CustomFieldValues;
}

/**
 * Props for the CompanyCreateSheet component.
 */
export interface CompanyCreateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: (company: Company) => void;
}

/**
 * Creates an empty address object with all fields set to null.
 * @returns A new Address with all fields null
 */
const createEmptyAddress = (): Address => ({
  name: null,
  address_line_1: null,
  address_line_2: null,
  city: null,
  state: null,
  zipcode: null,
  country: null,
});

/**
 * Returns the initial empty form data for a new company.
 * @returns Default CompanyFormData with empty values
 */
const getInitialFormData = (): CompanyFormData => ({
  name: "",
  phone: "",
  email: "",
  address: createEmptyAddress(),
  salesRepUserId: null,
  defaultPriceListId: null,
  customFields: {},
});

/**
 * A sheet component for creating companies. Presents an empty form and creates
 * the company via the store, optionally assigning a default price list.
 * @param props - Open state, change handler, and saved callback
 * @returns The CompanyCreateSheet component
 */
export default function CompanyCreateSheet({
  open,
  onOpenChange,
  onSaved,
}: CompanyCreateSheetProps) {
  const { createCompany } = useCompaniesStore();
  const setCurrentPriceListForCompany = usePriceListsStore(
    (state) => state.setCurrentPriceListForCompany,
  );

  const [formData, setFormData] =
    useState<CompanyFormData>(getInitialFormData());
  const [isSaving, setIsSaving] = useState(false);
  const customFieldsSchema = useCompanySettingsStore(
    (state) => state.settings?.customFieldsSchema ?? null,
  );
  const hasFetchedSettings = useCompanySettingsStore(
    (state) => state.hasFetched,
  );
  const fetchSettings = useCompanySettingsStore((state) => state.fetchSettings);

  const countryOptions = useMemo(
    () =>
      ISO_3166_1_ALPHA_2_CODES.map(
        (
          code,
        ): {
          code: IsoCountryCode;
          label: string;
        } => ({
          code,
          label: code,
        }),
      ),
    [],
  );

  // Reset form data when the sheet opens
  useEffect(() => {
    if (!open) {
      return;
    }
    setFormData(getInitialFormData());
  }, [open]);

  useEffect(() => {
    if (!open || hasFetchedSettings) {
      return;
    }
    fetchSettings();
  }, [open, hasFetchedSettings, fetchSettings]);

  /**
   * Updates a single field on the address portion of form data.
   * @param key - The address field to update
   * @param value - The new value for the field
   */
  const updateAddressField = <K extends keyof Address>(
    key: K,
    value: Address[K],
  ) => {
    setFormData((prev) => {
      const nextAddress = { ...(prev.address ?? createEmptyAddress()) };
      nextAddress[key] = value;
      return {
        ...prev,
        address: nextAddress,
      };
    });
  };

  /**
   * Handles country selection, validating the value as an ISO 3166-1 alpha-2 code.
   * @param value - The selected country code string
   */
  const handleCountrySelect = (value: string) => {
    if (value === "") {
      updateAddressField("country", null);
      return;
    }

    if (!isISO31661Alpha2Code(value)) {
      return;
    }

    updateAddressField("country", value);
  };

  /**
   * Handles saving a new company. Validates, creates via API, and assigns price list if selected.
   */
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Company name is required");
      return;
    }

    const trimmedEmail = (formData.email ?? "").trim();
    const payload: CreateCompanyData = {
      name: formData.name,
      customFields: formData.customFields,
    };

    if (formData.phone?.trim()) {
      payload.phone = formData.phone.trim();
    }

    if (trimmedEmail) {
      payload.email = trimmedEmail;
    }

    if (formData.address) {
      payload.address = formData.address;
    }

    if (formData.salesRepUserId) {
      payload.salesRepUserId = formData.salesRepUserId;
    }

    setIsSaving(true);
    try {
      const { company: result, error: createError } =
        await createCompany(payload);
      if (result) {
        if (formData.defaultPriceListId !== null) {
          await setCurrentPriceListForCompany({
            companyId: result.id,
            priceListId: formData.defaultPriceListId,
          });
        }
        toast.success("Company created successfully");
        onSaved(result);
        onOpenChange(false);
        setFormData(getInitialFormData());
      } else {
        toast.error(createError ?? "Failed to create company");
      }
    } catch (_error) {
      toast.error("Failed to create company");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:w-[600px] sm:max-w-[600px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Create New Company</SheetTitle>
          <SheetDescription>Add a new company to your records</SheetDescription>
        </SheetHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="create-name">Company Name *</Label>
            <Input
              id="create-name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="Company name"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="create-phone">Phone</Label>
            <Input
              id="create-phone"
              value={formData.phone || ""}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              placeholder="Phone number (optional)"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="create-email">Email</Label>
            <Input
              id="create-email"
              type="email"
              value={formData.email || ""}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              placeholder="Email address (optional)"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="create-salesRep">Sales Rep</Label>
            <UserSelector
              currentlySelectedId={formData.salesRepUserId ?? null}
              onSelectId={(salesRepUserId: string | null) =>
                setFormData({ ...formData, salesRepUserId })
              }
              placeholder="Select a sales rep (optional)"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="create-defaultPriceList">Default Price List</Label>
            <PriceListSelector
              currentlySelectedId={formData.defaultPriceListId ?? null}
              onSelectId={(defaultPriceListId: string | null) =>
                setFormData({ ...formData, defaultPriceListId })
              }
              placeholder="Select a default price list (optional)"
            />
          </div>

          <div className="grid gap-2">
            <Label>Address</Label>
            <div className="space-y-2">
              <Input
                placeholder="Address Line 1"
                value={formData.address?.address_line_1 || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  updateAddressField("address_line_1", value ? value : null);
                }}
              />
              <Input
                placeholder="Address Line 2"
                value={formData.address?.address_line_2 || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  updateAddressField("address_line_2", value ? value : null);
                }}
              />
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Input
                  placeholder="City"
                  value={formData.address?.city || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateAddressField("city", value ? value : null);
                  }}
                />
                <Input
                  placeholder="State"
                  value={formData.address?.state || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateAddressField("state", value ? value : null);
                  }}
                />
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Input
                  placeholder="Zip Code"
                  value={formData.address?.zipcode || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    updateAddressField("zipcode", value ? value : null);
                  }}
                />
                <div className="grid gap-1">
                  <Select
                    value={formData.address?.country || ""}
                    onValueChange={handleCountrySelect}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countryOptions.map(({ code, label }) => (
                        <SelectItem key={code} value={code}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {customFieldsSchema &&
            Object.keys(customFieldsSchema.properties).length > 0 && (
              <CustomFieldsSection
                schema={customFieldsSchema}
                values={formData.customFields}
                readOnly={false}
                onChange={(fieldName, value) =>
                  setFormData((prev) => ({
                    ...prev,
                    customFields: { ...prev.customFields, [fieldName]: value },
                  }))
                }
              />
            )}
        </div>

        <SheetFooter className="flex-col gap-2 sm:flex-row">
          <SheetClose asChild>
            <Button
              variant="outline"
              disabled={isSaving}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
          </SheetClose>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full sm:w-auto"
          >
            {isSaving && <Loader className="mr-2 h-4 w-4 animate-spin" />}
            Create Company
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
